import { InfoOutlineIcon } from '@carboncopyco/icons';
import { PlaybookEmptyStateMonocolorIllustration } from '@carboncopyco/illustrations';
import type { PlaybookAssetType } from '@carboncopyco/types';
import {
  ColorHex,
  EmptyView,
  TextCaption,
  Tooltip,
  Typography,
} from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMutationObserver } from 'ahooks';
import type React from 'react';
import { useRef } from 'react';
import { usePlaybookModalState } from '../PlaybookModalContext';
import { ExternalUrlForm } from './ExternalUrlForm';
import { ExternalUrlRow } from './ExternalUrlRow';
import { InternalAssetRow } from './InternalAssetRow';

const assetRowByAssetType: Record<
  PlaybookAssetType,
  React.FC<{ id: string }>
> = {
  demo: InternalAssetRow,
  tour: InternalAssetRow,
  externalUrl: ExternalUrlRow,
};

type AssetListProps = {
  showAddUrl: boolean;
  onCloseAddUrl: () => void;
};

export const AssetList = ({ showAddUrl, onCloseAddUrl }: AssetListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );
  const { assets, setAssets, playbookId } = usePlaybookModalState();
  const infoIconRef = useRef();
  const assetListRef = useRef<HTMLDivElement>(null);
  const { trackReorderPlaybookAssets } = usePlaybookAnalytics(
    playbookId ? 'update_playbook_modal' : 'create_playbook_modal'
  );
  const scrollToAddExternalUrl = (mutationList, observer) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'childList') {
        const scrollTarget = document.querySelector(
          "[class*='add-external-url']"
        );
        if (scrollTarget) {
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
          });
          observer.disconnect();
          break;
        }
      }
    }
  };

  useMutationObserver(scrollToAddExternalUrl, assetListRef, {
    childList: true,
    subtree: true,
  });
  if (!assets.length && !showAddUrl) {
    return (
      <EmptyView
        noBg
        illustration={<PlaybookEmptyStateMonocolorIllustration />}
        title="No added resources"
        message="Once adding resources, arrange them here"
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={({ active, over }) => {
        trackReorderPlaybookAssets({ playbookId });
        if (over?.id !== active.id) {
          const oldIndex = assets.findIndex(
            ({ assetId }) => assetId === active.id
          );
          const newIndex = assets.findIndex(
            ({ assetId }) => assetId === over?.id
          );
          setAssets(arrayMove(assets, oldIndex, newIndex));
        }
      }}
    >
      <div className="px-5 flex items-center justify-between">
        <TextCaption
          label={`Selected assets (${assets?.length})`}
          color="text-ds-natural-300"
        />
        <div className="flex items-center gap-1">
          <div
            className="flex items-center gap-1"
            ref={infoIconRef}
          >
            <TextCaption
              label="Auto sync"
              color="text-ds-natural-300"
            />
            <InfoOutlineIcon
              size={12}
              color={ColorHex.natural300}
            />
          </div>
          <Tooltip
            noArrow
            anchorRef={infoIconRef}
            placement="top"
            delay={[350, undefined]}
          >
            <div className="p-3">
              <Typography
                fontSize="text-ds-200"
                lineHeight="leading-ds-200"
                color="text-ds-natural"
                fontWeight="font-ds-normal"
                letterSpacing="tracking-ds-tight"
                label={
                  <div>
                    Auto-sync will automatically <br />
                    sync any changes made to <br />
                    an asset and reflect them in <br /> the Playbook.
                  </div>
                }
              />
            </div>
          </Tooltip>
        </div>
      </div>
      <ul className="px-5 py-3 flex flex-col w-full gap-2 overflow-y-auto h-[370px]">
        <SortableContext
          items={assets.map((asset) => asset.assetId)}
          strategy={verticalListSortingStrategy}
        >
          {assets.map((asset) => {
            const Row = assetRowByAssetType[asset.assetType];
            return (
              <Row
                key={asset.assetId}
                id={asset.assetId}
              />
            );
          })}
        </SortableContext>
        {showAddUrl && (
          <div className="add-external-url">
            <ExternalUrlForm onClose={onCloseAddUrl} />
          </div>
        )}
      </ul>
    </DndContext>
  );
};
